import React, { useEffect, useState, useCallback } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useLanguage } from "../context/LanguageContext";
import { useHistory } from "../context/HistoryContext";
import { useMode } from "../context/ModeContext";
import { useCrypto } from "../context/CryptoContext";
import { calculateProfit, calculateRiskScenarios, CALC_TYPES, PERIODS } from "../utils/calculations";
import { exportToPDF, exportToExcel } from "../utils/exportUtils";
import { useExchangeRate } from "../context/ExchangeRateContext";
import useAnimatedNumber from "../hooks/useAnimatedNumber";
import {
    LuCheck,
    LuX,
    LuInfo,
    LuTriangleAlert,
    LuTrendingUp,
    LuTrendingDown,
    LuZap,
    LuActivity
} from "react-icons/lu";
import CoinSelector from "../components/CoinSelector";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// Animated Value Component
function AnimatedValue({ value, formatter }) {
    const animatedValue = useAnimatedNumber(value, 800);
    return <span className="animated-value">{formatter(animatedValue)}</span>;
}

// Toast Component
function Toast({ toast, onClose }) {
    if (!toast) return null;
    const icons = {
        success: <LuCheck />,
        error: <LuX />,
        info: <LuInfo />,
        warning: <LuTriangleAlert />
    };
    return (
        <div className={`toast ${toast.type}`}>
            <span className="toast-icon">{icons[toast.type]}</span>
            <div className="toast-content">
                <div className="toast-title">{toast.title}</div>
                <div className="toast-message">{toast.message}</div>
            </div>
            <button className="toast-close" onClick={onClose}><LuX /></button>
        </div>
    );
}

function CryptoHome() {
    const { lang, t } = useLanguage();
    const { addToHistory } = useHistory();
    const { getUsdToTry } = useExchangeRate();
    const { selectedCurrency } = useMode();
    const { coins, connected } = useCrypto();

    // Form State
    const [selectedCoin, setSelectedCoin] = useState('BTCUSDT');
    const [coinAmount, setCoinAmount] = useState(1);
    const [balance, setBalance] = useState(1000);
    const [investmentRatio, setInvestmentRatio] = useState(100);
    const [dailyPercent, setDailyPercent] = useState(0.5);
    const [targetDay, setTargetDay] = useState(30);
    const [calculationType, setCalculationType] = useState(CALC_TYPES.COMPOUND);
    const [period, setPeriod] = useState(PERIODS.DAILY);

    // Get selected coin's live price
    const currentCoin = coins.find(c => c.symbol === selectedCoin);
    const livePrice = currentCoin?.price || 0;

    // Update balance when coin or amount changes
    useEffect(() => {
        if (livePrice > 0) {
            setBalance(coinAmount * livePrice);
        }
    }, [selectedCoin, coinAmount, livePrice]);

    // Results
    const [results, setResults] = useState([]);
    const [stats, setStats] = useState(null);
    const [riskScenarios, setRiskScenarios] = useState([]);

    // UI State
    const [toast, setToast] = useState(null);

    const usdToTry = getUsdToTry();

    // Format USDT values (crypto calculations are always in USDT)
    const formatUSDT = useCallback((value) => {
        return `$${(value || 0).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    }, [lang]);

    const showToast = (type, title, message) => {
        setToast({ type, title, message });
        setTimeout(() => setToast(null), 3000);
    };

    const handleCalculate = useCallback(() => {
        // Validate inputs
        const balanceValue = Number(balance);
        if (!balanceValue || balanceValue <= 0) {
            showToast('warning',
                lang === 'tr' ? 'Uyarı' : 'Warning',
                lang === 'tr' ? 'Lütfen coin seçin ve miktar girin. Fiyat verisi bekleniyor...' : 'Please select a coin and enter amount. Waiting for price data...'
            );
            return;
        }

        const params = {
            balance: balanceValue,
            investmentRatio: Number(investmentRatio),
            percentage: Number(dailyPercent),
            targetDay: Number(targetDay),
            calcType: calculationType,
            period
        };

        const data = calculateProfit(params);
        const scenarios = calculateRiskScenarios(params);

        const calculatedStats = {
            initialBalance: params.balance,
            finalBalance: data[data.length - 1]?.usdt || params.balance,
            totalProfit: (data[data.length - 1]?.usdt || params.balance) - params.balance,
            dailyAverage: data.length > 0 ? data.reduce((sum, d) => sum + d.profit, 0) / data.length : 0,
            profitRate: params.balance > 0 ? ((data[data.length - 1]?.usdt || params.balance) - params.balance) / params.balance * 100 : 0
        };

        setResults(data.map(d => ({
            ...d,
            try: d.usdt * usdToTry
        })));
        setStats(calculatedStats);
        setRiskScenarios(scenarios);

        // Add to history
        addToHistory({
            params,
            stats: calculatedStats,
            currency: selectedCurrency,
            mode: 'crypto',
            coin: currentCoin ? {
                symbol: currentCoin.symbol,
                shortName: currentCoin.shortName,
                name: currentCoin.name,
                price: livePrice,
                amount: coinAmount
            } : null
        });

        showToast('success', t('toast.calculated'), t('toast.calculatedDesc'));
    }, [balance, investmentRatio, dailyPercent, targetDay, calculationType, period, usdToTry, addToHistory, t, selectedCurrency, currentCoin, livePrice, coinAmount, lang]);

    // Chart data
    const chartData = {
        labels: results.map(r => `${r.day}`),
        datasets: [
            {
                label: 'USDT',
                data: results.map(r => r.usdt),
                borderColor: '#00ff88',
                backgroundColor: 'rgba(0, 255, 136, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: '#00ff88',
                borderWidth: 2
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#00ff88',
                bodyColor: '#fff',
                borderColor: '#00ff88',
                borderWidth: 1,
                callbacks: {
                    label: (context) => formatUSDT(context.raw)
                }
            }
        },
        scales: {
            x: {
                grid: { color: 'rgba(255, 255, 255, 0.05)' },
                ticks: { color: 'rgba(255, 255, 255, 0.5)' }
            },
            y: {
                grid: { color: 'rgba(255, 255, 255, 0.05)' },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.5)',
                    callback: (value) => formatUSDT(value)
                }
            }
        }
    };

    return (
        <div className="crypto-home">
            <div className="page-header home-header">
                <div className="page-header-content">
                    <div className="page-title-group">
                        <div>
                            <h1 className="page-title">{lang === 'tr' ? 'Kripto Getiri Hesaplama' : 'Crypto Profit Calculator'}</h1>
                        </div>
                    </div>
                    {connected && (
                        <div className="page-actions">
                            <span className="live-indicator">
                                <LuZap className="live-icon" />
                                <span>{lang === 'tr' ? 'Canlı Veri' : 'Live Data'}</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="calculator-grid">
                {/* Form Card */}
                <div className="card form-card">
                    <div className="card-header">
                        <h2><LuActivity /> {lang === 'tr' ? 'Hesaplama Parametreleri' : 'Calculation Parameters'}</h2>
                        {connected && (
                            <span className="live-badge">
                                <span className="live-dot"></span>
                                {lang === 'tr' ? 'Canlı' : 'Live'}
                            </span>
                        )}
                    </div>

                    {/* Coin Selection */}
                    <div className="coin-selector-section">
                        <div className="form-group coin-select-group">
                            <label className="form-label">{lang === 'tr' ? 'Coin Seçin' : 'Select Coin'}</label>
                            <CoinSelector
                                coins={coins}
                                selectedCoin={selectedCoin}
                                onSelect={setSelectedCoin}
                                lang={lang}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{lang === 'tr' ? 'Miktar' : 'Amount'}</label>
                            <input
                                type="number"
                                className="form-input"
                                value={coinAmount}
                                onChange={(e) => setCoinAmount(Number(e.target.value))}
                                min="0"
                                step="0.01"
                            />
                        </div>
                        {currentCoin && (
                            <div className="coin-live-info">
                                <div className="coin-price-display">
                                    <span className="coin-icon-small">
                                        <img
                                            src={`https://assets.coincap.io/assets/icons/${currentCoin.shortName.toLowerCase()}@2x.png`}
                                            alt={currentCoin.shortName}
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </span>
                                    <div className="coin-price-info">
                                        <span className="coin-current-price">${livePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        <span className={`coin-change ${currentCoin.priceChangePercent >= 0 ? 'positive' : 'negative'}`}>
                                            {currentCoin.priceChangePercent >= 0 ? '+' : ''}{currentCoin.priceChangePercent.toFixed(2)}%
                                        </span>
                                    </div>
                                </div>
                                <div className="coin-total-value">
                                    <span className="total-label">{lang === 'tr' ? 'Toplam Değer' : 'Total Value'}</span>
                                    <span className="total-value">${(coinAmount * livePrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label className="form-label">{t('form.balance')} (USDT)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={(balance || 0).toFixed(2)}
                                readOnly
                                disabled
                            />
                            <span className="form-hint">{lang === 'tr' ? 'Seçilen coin miktarından otomatik hesaplanır' : 'Auto-calculated from selected coin amount'}</span>
                        </div>
                        <div className="form-group">
                            <label className="form-label">{t('form.investmentRatio')} (%)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={investmentRatio}
                                onChange={(e) => setInvestmentRatio(e.target.value)}
                                min="0"
                                max="100"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{t('form.dailyPercent')} (%)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={dailyPercent}
                                onChange={(e) => setDailyPercent(e.target.value)}
                                step="0.1"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{t('form.targetDay')}</label>
                            <input
                                type="number"
                                className="form-input"
                                value={targetDay}
                                onChange={(e) => setTargetDay(e.target.value)}
                                min="1"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{t('form.calculationType')}</label>
                            <select
                                className="form-select"
                                value={calculationType}
                                onChange={(e) => setCalculationType(e.target.value)}
                            >
                                <option value={CALC_TYPES.COMPOUND}>{t('form.compound')}</option>
                                <option value={CALC_TYPES.SIMPLE}>{t('form.simple')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">{t('form.period')}</label>
                            <select
                                className="form-select"
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                            >
                                <option value={PERIODS.DAILY}>{t('form.daily')}</option>
                                <option value={PERIODS.WEEKLY}>{t('form.weekly')}</option>
                                <option value={PERIODS.MONTHLY}>{t('form.monthly')}</option>
                            </select>
                        </div>
                    </div>
                    <button className="btn btn-primary btn-calculate" onClick={handleCalculate}>
                        <LuZap /> {t('form.calculate')}
                    </button>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="stats-grid">
                        <div className="stat-card crypto-stat">
                            <div className="stat-icon"><LuTrendingUp /></div>
                            <div className="stat-content">
                                <span className="stat-label">{lang === 'tr' ? 'Toplam Kâr' : 'Total Profit'}</span>
                                <AnimatedValue value={stats.totalProfit} formatter={formatUSDT} />
                            </div>
                        </div>
                        <div className="stat-card crypto-stat">
                            <div className="stat-icon"><LuActivity /></div>
                            <div className="stat-content">
                                <span className="stat-label">{lang === 'tr' ? 'Son Bakiye' : 'Final Balance'}</span>
                                <AnimatedValue value={stats.finalBalance} formatter={formatUSDT} />
                            </div>
                        </div>
                        <div className="stat-card crypto-stat">
                            <div className="stat-icon"><LuZap /></div>
                            <div className="stat-content">
                                <span className="stat-label">{lang === 'tr' ? 'Kâr Oranı' : 'Profit Rate'}</span>
                                <AnimatedValue value={stats.profitRate} formatter={(v) => `${(v || 0).toFixed(2)}%`} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Chart Card */}
                {results.length > 0 && (
                    <div className="card chart-card">
                        <div className="card-header">
                            <h2><LuTrendingUp /> {lang === 'tr' ? 'Bakiye Grafiği' : 'Balance Chart'}</h2>
                        </div>
                        <div className="chart-container" style={{ height: '300px' }}>
                            <Line data={chartData} options={chartOptions} />
                        </div>
                    </div>
                )}

                {/* Results Table */}
                {results.length > 0 && (
                    <div className="card results-card">
                        <div className="card-header">
                            <h2>{lang === 'tr' ? 'Günlük Detaylar' : 'Daily Details'}</h2>
                            <div className="card-actions">
                                <button className="btn btn-sm" onClick={() => exportToPDF(results, stats, lang)}>PDF</button>
                                <button className="btn btn-sm" onClick={() => exportToExcel(results, stats, lang)}>Excel</button>
                            </div>
                        </div>
                        <div className="table-container">
                            <table className="results-table">
                                <thead>
                                    <tr>
                                        <th>{lang === 'tr' ? 'Gün' : 'Day'}</th>
                                        <th>USDT</th>
                                        <th>{lang === 'tr' ? 'Kâr' : 'Profit'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.slice(0, 30).map((row) => (
                                        <tr key={row.day}>
                                            <td>{row.day}</td>
                                            <td className="text-accent">{formatUSDT(row.usdt)}</td>
                                            <td className="text-green">+{formatUSDT(row.profit)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Risk Scenarios */}
                {riskScenarios.length > 0 && (
                    <div className="card risk-card">
                        <div className="card-header">
                            <h2><LuTriangleAlert /> {lang === 'tr' ? 'Risk Senaryoları' : 'Risk Scenarios'}</h2>
                        </div>
                        <div className="risk-scenarios">
                            {riskScenarios.map((scenario, index) => (
                                <div key={index} className={`risk-scenario risk-${scenario.level}`}>
                                    <div className="risk-label">{scenario.name}</div>
                                    <div className="risk-value">{formatUSDT(scenario.finalBalance)}</div>
                                    <div className="risk-change">
                                        {(scenario.profitPercent || 0) >= 0 ? <LuTrendingUp /> : <LuTrendingDown />}
                                        {(scenario.profitPercent || 0).toFixed(2)}%
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
        </div>
    );
}

export default CryptoHome;
